import { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X, BookOpen, CheckCircle2, BarChart3, ShieldCheck } from 'lucide-react';

const solutions = [
  {
    slug: 'bookkeeping',
    title: 'Bookkeeping',
    desc: 'AI-powered transaction capture, categorization, and review.',
    icon: BookOpen,
  },
  {
    slug: 'close',
    title: 'Close',
    desc: 'Month-end recon, exception queues, and close readiness scoring.',
    icon: CheckCircle2,
  },
  {
    slug: 'reporting',
    title: 'Reporting',
    desc: 'Financial statements, budgeting, and multi-entity insights.',
    icon: BarChart3,
  },
  {
    slug: 'controls',
    title: 'Controls',
    desc: 'Credit card tracking, missing-document chaser, and audit trail.',
    icon: ShieldCheck,
  },
];

export default function PublicHeader() {
  const [openMenu, setOpenMenu] = useState(false);
  const [mobile, setMobile] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpenMenu(false); setMobile(false); }, [location.pathname]);

  const linkCls = ({ isActive }) =>
    `text-[14px] font-body font-medium tracking-tight transition-colors ${
      isActive ? 'text-[#0A3622]' : 'text-[#1A1A1A]/80 hover:text-[#0A3622]'
    }`;

  return (
    <header
      className={`sticky top-0 z-40 transition-all duration-200 ${scrolled ? 'backdrop-blur bg-[#F7F6F2]/85' : 'bg-transparent'}`}
      style={{ borderBottom: scrolled ? '1px solid #E5E3DB' : '1px solid transparent' }}
      data-testid="public-header"
    >
      <div className="max-w-[1280px] mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="public-logo">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#0A3622' }}>
            <span className="text-white font-heading text-[15px] font-bold">S</span>
          </div>
          <span className="font-heading text-[17px] font-semibold tracking-tight" style={{ color: '#111815' }}>SnuggleMind</span>
        </Link>

        {/* Desktop */}
        <nav className="hidden lg:flex items-center gap-8" data-testid="public-nav">
          <div
            className="relative"
            onMouseEnter={() => setOpenMenu(true)}
            onMouseLeave={() => setOpenMenu(false)}
          >
            <button
              className="flex items-center gap-1 text-[14px] font-body font-medium text-[#1A1A1A]/80 hover:text-[#0A3622] transition-colors"
              data-testid="nav-solutions-trigger"
            >
              Solutions <ChevronDown size={14} className={`transition-transform ${openMenu ? 'rotate-180' : ''}`} />
            </button>
            {openMenu && (
              <div
                className="absolute left-1/2 -translate-x-1/2 top-full pt-3"
                data-testid="solutions-megamenu"
              >
                <div
                  className="w-[640px] rounded-2xl p-3 grid grid-cols-2 gap-1.5"
                  style={{ background: '#FFFFFF', border: '1px solid #E5E3DB', boxShadow: '0 20px 50px -12px rgba(10,54,34,0.15)' }}
                >
                  {solutions.map((s) => (
                    <Link
                      key={s.slug}
                      to={`/solutions/${s.slug}`}
                      className="flex items-start gap-3 p-4 rounded-xl hover:bg-[#F7F6F2] transition-colors group"
                      data-testid={`megamenu-${s.slug}`}
                    >
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: 'rgba(217,138,74,0.12)' }}>
                        <s.icon size={18} style={{ color: '#D98A4A' }} />
                      </div>
                      <div>
                        <p className="font-heading text-[15px] font-semibold mb-0.5" style={{ color: '#0A3622' }}>{s.title}</p>
                        <p className="text-[12.5px] leading-snug" style={{ color: '#4B5563' }}>{s.desc}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
          <NavLink to="/connect" className={linkCls} data-testid="nav-connect">Connect</NavLink>
          <NavLink to="/about" className={linkCls} data-testid="nav-about">About</NavLink>
          <NavLink to="/resources" className={linkCls} data-testid="nav-resources">Resources</NavLink>
          <NavLink to="/tour" className={linkCls} data-testid="nav-tour">Tour</NavLink>
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link
            to="/login"
            className="text-[14px] font-body font-medium text-[#1A1A1A]/80 hover:text-[#0A3622] transition-colors"
            data-testid="nav-login"
          >
            Log in
          </Link>
          <Link
            to="/register"
            className="group inline-flex items-center gap-2 h-10 px-5 rounded-full text-[13px] font-body font-semibold tracking-tight transition-all duration-200"
            style={{ background: '#D98A4A', color: '#FFFFFF' }}
            data-testid="nav-cta-trial"
          >
            <span>Start Free Trial</span>
            <span className="transition-transform group-hover:translate-x-0.5">→</span>
          </Link>
        </div>

        {/* Mobile hamburger */}
        <button
          className="lg:hidden p-2"
          onClick={() => setMobile(!mobile)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {mobile ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile drawer */}
      {mobile && (
        <div className="lg:hidden border-t" style={{ borderColor: '#E5E3DB', background: '#F7F6F2' }} data-testid="mobile-menu">
          <div className="px-6 py-4 space-y-3">
            <p className="text-[10px] uppercase tracking-[0.18em] font-semibold" style={{ color: '#9CA3AF' }}>Solutions</p>
            {solutions.map((s) => (
              <Link key={s.slug} to={`/solutions/${s.slug}`} className="block text-[15px] font-heading font-medium py-1" style={{ color: '#0A3622' }} data-testid={`mobile-solution-${s.slug}`}>
                {s.title}
              </Link>
            ))}
            <div className="h-px my-3" style={{ background: '#E5E3DB' }} />
            <Link to="/connect" className="block text-[15px] font-body font-medium py-1" style={{ color: '#1A1A1A' }}>Connect</Link>
            <Link to="/about" className="block text-[15px] font-body font-medium py-1" style={{ color: '#1A1A1A' }}>About</Link>
            <Link to="/resources" className="block text-[15px] font-body font-medium py-1" style={{ color: '#1A1A1A' }}>Resources</Link>
            <Link to="/tour" className="block text-[15px] font-body font-medium py-1" style={{ color: '#1A1A1A' }}>Tour</Link>
            <Link to="/login" className="block text-[15px] font-body font-medium py-1" style={{ color: '#1A1A1A' }}>Log in</Link>
            <Link to="/register" className="block text-center mt-3 h-11 leading-[44px] rounded-full font-body font-semibold text-[14px]" style={{ background: '#D98A4A', color: '#fff' }} data-testid="mobile-cta-trial">
              Start Free Trial →
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
